/**
 * Escolha do provider (SPEC 7, 10.2).
 *
 * - `?provider=mock` (padrão na V1): MockProvider, ponte `postMessage` para o worker do `packages/sim`.
 * - `?provider=fixture`: FixtureProvider, JSONs estáticos do cenário `normal` às 14:32 (Apêndice B).
 * - `?provider=api` (ou o build `VITE_ROTA_LIVE=true`): modo ao vivo (`data/live.ts`). O login e as
 *   Automações falam com o servidor real (`ApiAuthProvider`, `ApiAutomations`); o Painel e o
 *   Estúdio continuam na simulação (mock), com um aviso discreto nas telas.
 *
 * Enquanto o `packages/sim` não estiver pronto, o mock não sobe e o app cai no FixtureProvider,
 * com aviso no console. O fixture e o ApiProvider vêm sob demanda. O MockProvider (a ponte, ~2 KB;
 * o worker com a simulação sobe à parte, no boot.js) vem no bundle inicial: num chunk próprio, era
 * mais um pedido disputando as conexões do 4G com o JS da primeira tela até o LCP (10.4).
 *
 * Prioridade da primeira tela (10.4): até a primeira pintura útil, só o boot e o snapshot vão ao
 * provider. Séries, listas e relatórios esperam a pintura (`deferUntilPainted`), para não disputar
 * a rede e a CPU (no mock, os chunks de consulta do worker) com os números que o LCP mede.
 */
import type {
  Area,
  AreaSummary,
  AuthProvider,
  AutomationsProvider,
  BootPayload,
  DataProvider,
  Id,
  PainelProviderV2,
  Role,
  SessionInfo,
  StudioProvider,
  SuiteNotification,
  SuiteProvider,
  Unsubscribe,
} from '@rota/contracts';
import { whenPainted } from '@/lib/afterPaint';
import { ApiAuthProvider } from './auth/ApiAuthProvider';
import { MockAuthProvider } from './auth/MockAuthProvider';
import { currentSession, sessionStore } from './auth/session';
import type { ApiAutomationsExtras } from './automacoes/ApiAutomations';
import { isLiveMode } from './live';
import { withRealSales } from './live/realSales';
import { createMockProvider } from './mock/MockProvider';

export type ProviderKind = 'mock' | 'fixture' | 'api';

export interface CreatedProvider {
  provider: DataProvider;
  kind: ProviderKind;
  /** Motivo de ter caído para o fixture, quando o mock não subiu. */
  fallbackReason?: string;
}

export function providerKindFromUrl(
  search: string = typeof location === 'undefined' ? '' : location.search,
): ProviderKind {
  const v = new URLSearchParams(search).get('provider');
  if (v === 'fixture' || v === 'mock') return v;
  // `?provider=api` liga o modo ao vivo (login e Automações reais); o Painel segue na simulação.
  return 'mock';
}

/** Opções da simulação lidas da URL (`?cenario=`, `?seed=`, `?hora=`, `?vel=`); o provider aplica `sim:control`. */
export interface SimUrlOptions {
  scenario?: string;
  seed?: string;
  fixedAt?: string;
  speed?: number;
  /** Dia simulado (AAAA-MM-DD). No modo ao vivo é o dia de hoje, para as vendas reais caberem no mesmo dia. */
  dateKey?: string;
}

export function simOptionsFromUrl(
  search: string = typeof location === 'undefined' ? '' : location.search,
): SimUrlOptions {
  const p = new URLSearchParams(search);
  const out: SimUrlOptions = {};
  const scenario = p.get('cenario');
  if (scenario) out.scenario = scenario;
  const seed = p.get('seed');
  if (seed) out.seed = seed;
  const hora = p.get('hora');
  if (hora && /^([01]\d|2[0-3]):[0-5]\d$/.test(hora)) out.fixedAt = hora;
  const vel = Number(p.get('vel'));
  if (vel === 1 || vel === 10 || vel === 60) out.speed = vel;
  return out;
}

async function createFixture(): Promise<DataProvider> {
  const { createFixtureProvider } = await import('./fixtures/FixtureProvider');
  return createFixtureProvider();
}

/**
 * Leituras que nenhuma primeira tela precisa para pintar: todos os `get*` menos o snapshot, e as
 * listas. O Hoje pinta com o snapshot; as telas lazy pintam a moldura e os títulos antes dos dados.
 * Boot, assinatura, `status` e comandos nunca esperam.
 */
export const isDeferredRead = (method: string): boolean => /^(get(?!Snapshot$)|list$)/.test(method);

/** Sem tela que marque a pintura (404, "Sem acesso"), as leituras seguem depois disto. */
const DEFER_FALLBACK_MS = 1500;

/**
 * Embrulha o provider: as leituras adiáveis (`isDeferredRead`) chamadas antes da primeira pintura útil esperam por ela
 * (`whenPainted`, que toda tela marca: o Hoje com o snapshot, as lazy ao renderizar). Depois da
 * pintura, tudo passa direto, sem custo. Os métodos ficam ligados ao provider original (classes
 * com campos privados seguem funcionando) e com identidade estável.
 */
export function deferUntilPainted(
  provider: DataProvider,
  onPainted: (cb: () => void) => void = (cb) => whenPainted(cb, DEFER_FALLBACK_MS),
): DataProvider {
  let painted = false;
  const ready = new Promise<void>((resolve) =>
    onPainted(() => {
      painted = true;
      resolve();
    }),
  );
  const cache = new Map<PropertyKey, unknown>();
  return new Proxy(provider, {
    get(target, prop) {
      const value = Reflect.get(target, prop, target) as unknown;
      if (typeof value !== 'function') return value;
      const hit = cache.get(prop);
      if (hit) return hit;
      const fn = value as (...args: unknown[]) => unknown;
      const wrapped =
        typeof prop === 'string' && isDeferredRead(prop)
          ? (...args: unknown[]) =>
              painted ? fn.apply(target, args) : ready.then(() => fn.apply(target, args))
          : fn.bind(target);
      cache.set(prop, wrapped);
      return wrapped;
    },
  });
}

async function createRaw(kind: ProviderKind): Promise<CreatedProvider> {
  if (kind === 'fixture') return { provider: await createFixture(), kind };
  if (kind === 'api') {
    const { ApiProvider } = await import('./api/ApiProvider');
    return { provider: new ApiProvider(), kind };
  }
  try {
    // Ao vivo: o Painel mostra o dia de hoje (as vendas reais da Kiwify entram por cima da simulação)
    // e ignora cenário, semente, hora e velocidade da URL (só a demonstração os usa).
    const opts: SimUrlOptions = isLiveMode() ? { dateKey: todayKeySP() } : simOptionsFromUrl();
    const provider = await createMockProvider(opts);
    return { provider, kind: 'mock' };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    console.warn(
      `[Rota] Simulação indisponível (${reason}). Usando o FixtureProvider (cenário normal, 14:32).`,
    );
    return { provider: await createFixture(), kind: 'fixture', fallbackReason: reason };
  }
}

/** Hoje em São Paulo (UTC−3, sem horário de verão), AAAA-MM-DD. */
export function todayKeySP(now: number = Date.now()): string {
  return new Date(now - 3 * 3_600_000).toISOString().slice(0, 10);
}

export async function createProvider(kind: ProviderKind = providerKindFromUrl()): Promise<CreatedProvider> {
  const raw = await createRaw(kind);
  // V2: os métodos novos do Painel (`getBackend`, `getPlan`…) que o provider ainda não tem vêm da
  // fixture escrita à mão (V2-WP0) até o V2-WP2 implementá-los no worker. No ApiProvider, não.
  const withV2Raw = raw.kind === 'api' ? raw : { ...raw, provider: withPainelV2(raw.provider) };
  // Ao vivo (fase 1): as vendas do Painel vêm da Kiwify real; o resto segue na simulação.
  const withV2 =
    isLiveMode() && raw.kind === 'mock'
      ? { ...withV2Raw, provider: withRealSales(withV2Raw.provider) }
      : withV2Raw;
  // A pessoa logada na suíte é o Viewer do Painel (V2 2.2): papel e nome vêm da sessão.
  const created = { ...withV2, provider: withSessionViewer(withV2.provider) };
  // Nos testes (jsdom) nada pinta: sem a espera, para as consultas responderem na hora.
  if (import.meta.env.MODE === 'test') return created;
  return { ...created, provider: deferUntilPainted(created.provider) };
}

/**
 * O Painel abre com o papel da sessão da suíte (V2 2.2): no `init`, se a pessoa logada tem outro
 * papel que o padrão do provider, troca o viewer (`switchViewer`) e põe nome e iniciais da sessão.
 * Sem sessão (TV, testes), nada muda. O "Ver como" do dono continua funcionando por cima.
 */
export function withSessionViewer(provider: DataProvider): DataProvider {
  const cache = new Map<PropertyKey, unknown>();
  return new Proxy(provider, {
    get(target, prop) {
      const own = Reflect.get(target, prop, target) as unknown;
      if (typeof own !== 'function') return own;
      const hit = cache.get(prop) as { src: unknown; fn: unknown } | undefined;
      if (hit?.src === own) return hit.fn;
      const bound = (own as (...a: unknown[]) => unknown).bind(target);
      const named = (b: BootPayload): BootPayload => {
        const s = currentSession();
        return s && s.viewer.role === b.viewer.role
          ? { ...b, viewer: { ...b.viewer, name: s.viewer.name, initials: s.viewer.initials }, session: s }
          : b;
      };
      const wrapped =
        prop === 'init'
          ? async (viewerId: Id) => {
              const s = currentSession();
              const boot = await target.init(viewerId);
              if (!s || s.viewer.role === 'tv' || s.viewer.role === boot.viewer.role) return named(boot);
              return named(await target.switchViewer(s.viewer.role));
            }
          : prop === 'switchViewer'
            ? async (role: Role) => named(await target.switchViewer(role))
            : bound;
      cache.set(prop, { src: own, fn: wrapped });
      return wrapped;
    },
  });
}

/** Métodos opcionais do `PainelProviderV2` (V2 6.2). */
export const PAINEL_V2_METHODS = [
  'getEconomics',
  'getKpisV2',
  'getChannels',
  'getBackend',
  'backend',
  'getLtv',
  'getTestLane',
  'getSlices',
  'getPlan',
  'plan',
  'listTrackedLinks',
  'upsertTrackedLink',
  'updateEconomics',
  'getFunnelV2',
  'getAutomationSummary',
] as const satisfies readonly (keyof PainelProviderV2)[];

/**
 * Completa o provider do Painel com os métodos V2 que ele ainda não tem. O que o provider real já
 * implementa (V2-WP2) sempre ganha. A fixture só desce na primeira chamada de um método V2, fora do
 * caminho da primeira pintura. Os métodos do provider original ficam ligados a ele (classes com
 * campos privados seguem funcionando).
 */
export function withPainelV2(provider: DataProvider): PainelProviderV2 {
  const names = new Set<string>(PAINEL_V2_METHODS);
  let fixture: Promise<Record<string, (...a: unknown[]) => Promise<unknown>>> | null = null;
  const fx = () => {
    fixture ??= import('./painelV2/fixture').then(
      (m) => m.createPainelV2Fixture() as unknown as Record<string, (...a: unknown[]) => Promise<unknown>>,
    );
    return fixture;
  };
  const cache = new Map<PropertyKey, unknown>();
  return new Proxy(provider as PainelProviderV2, {
    get(target, prop) {
      const own = Reflect.get(target, prop, target) as unknown;
      if (typeof own === 'function') {
        // Cache por função de origem: trocar o método no alvo (ex.: `vi.spyOn`) troca o ligado.
        const hit = cache.get(prop) as { src: unknown; fn: unknown } | undefined;
        if (hit?.src === own) return hit.fn;
        const fn = (own as (...a: unknown[]) => unknown).bind(target);
        cache.set(prop, { src: own, fn });
        return fn;
      }
      if (own !== undefined || typeof prop !== 'string' || !names.has(prop)) return own;
      const v2 = cache.get(prop) as { src: unknown; fn: unknown } | undefined;
      if (v2?.src === undefined && v2) return v2.fn;
      const fn = async (...args: unknown[]) => {
        const f = await fx();
        const impl = f[prop];
        if (!impl) throw { code: 'source_unavailable', message: `Método ${prop} indisponível.` };
        return impl(...args);
      };
      cache.set(prop, { src: undefined, fn });
      return fn;
    },
    has(target, prop) {
      return Reflect.has(target, prop) || (typeof prop === 'string' && names.has(prop));
    },
  });
}

// ===========================================================================
// Suíte (SPEC-v2 6.3, 9.1): `createSuite()` e o provider raiz das 3 áreas
// ===========================================================================

/**
 * Contrato que o V2-WP2 implementa para trocar a fixture pelo mock da simulação SEM editar este
 * arquivo: basta existir `data/automacoes/MockAutomations.ts` exportando `createMockAutomations` (e
 * `data/estudio/MockStudio.ts` exportando `createMockStudio`). `?provider=fixture` força a fixture.
 */
export interface AreaProviderOptions {
  /** Sessão atual: o provider checa as permissões da suíte e do Estúdio (a UI só esconde). */
  session: () => SessionInfo | null;
}
type AutomationsFactory = (o: AreaProviderOptions) => AutomationsProvider | Promise<AutomationsProvider>;
type StudioFactory = (o: AreaProviderOptions) => StudioProvider | Promise<StudioProvider>;

const automationsMocks = import.meta.glob<{ createMockAutomations?: AutomationsFactory }>(
  './automacoes/MockAutomations.ts',
);
const studioMocks = import.meta.glob<{ createMockStudio?: StudioFactory }>('./estudio/MockStudio.ts');

/** Sessão terminou no servidor (401) no meio do uso: volta ao `/entrar`. */
function onUnauthorized(): void {
  if (sessionStore.get().status === 'signed_in') sessionStore.signedOut(false);
}

async function loadAutomations(kind: ProviderKind, live: boolean): Promise<AutomationsProvider> {
  const opts: AreaProviderOptions = { session: currentSession };
  if (live) {
    const { createApiAutomations } = await import('./automacoes/ApiAutomations');
    return createApiAutomations({ ...opts, onUnauthorized });
  }
  const load = automationsMocks['./automacoes/MockAutomations.ts'];
  if (kind === 'mock' && load) {
    const m = await load();
    if (m.createMockAutomations) return m.createMockAutomations(opts);
    console.warn('[Rota] MockAutomations sem createMockAutomations; usando a fixture.');
  }
  const { createFixtureAutomations } = await import('./automacoes/fixtures/FixtureAutomations');
  return createFixtureAutomations(opts);
}

async function loadStudio(kind: ProviderKind): Promise<StudioProvider> {
  const opts: AreaProviderOptions = { session: currentSession };
  const load = studioMocks['./estudio/MockStudio.ts'];
  if (kind === 'mock' && load) {
    const m = await load();
    if (m.createMockStudio) return m.createMockStudio(opts);
    console.warn('[Rota] MockStudio sem createMockStudio; usando a fixture.');
  }
  const { createFixtureStudio } = await import('./estudio/fixtures/FixtureStudio');
  return createFixtureStudio(opts);
}

/** Provider do Painel (o mesmo do store do Painel; sobe o worker se ainda não subiu). */
async function loadPainel(): Promise<PainelProviderV2> {
  const { rotaStore } = await import('./store');
  const ready = (): DataProvider | null => rotaStore.getState().session.provider;
  if (!ready()) void rotaStore.actions().boot();
  const p =
    ready() ??
    (await new Promise<DataProvider>((resolve, reject) => {
      const off = rotaStore.subscribe((s) => {
        if (s.session.provider) {
          off();
          resolve(s.session.provider);
        } else if (s.session.status === 'error') {
          off();
          reject(s.session.error);
        }
      });
    }));
  return p as PainelProviderV2;
}

function createAuth(live: boolean): AuthProvider {
  // Modo ao vivo: login real no servidor do painel. Fora dele, o mock local (ADR-009).
  return live ? new ApiAuthProvider() : new MockAuthProvider();
}

/** Resumo das Automações com os números reais do servidor (modo ao vivo). */
async function liveAutomationsSummary(p: AutomationsProvider): Promise<AreaSummary | null> {
  const extras = p as AutomationsProvider & Partial<ApiAutomationsExtras>;
  if (!extras.liveSummary) return null;
  const s = await extras.liveSummary();
  const n = new Intl.NumberFormat('pt-BR');
  const parts = [
    s.killed ? 'Desligadas' : s.dryRun ? 'Modo teste' : null,
    `${n.format(s.comments24h)} ${s.comments24h === 1 ? 'comentário' : 'comentários'} em 24 h`,
    s.waiting > 0 ? `${n.format(s.waiting)} aguardando a equipe` : null,
  ].filter(Boolean);
  return {
    area: 'automacoes',
    line: parts.join(' · '),
    badge: s.killed
      ? { count: 1, tone: 'critical' }
      : s.waiting > 0
        ? { count: s.waiting, tone: 'warning' }
        : null,
  };
}

/**
 * Palco (docs/palco/SPEC.md 4): a linha do lançador vem do próprio provider do Palco (mock ou API;
 * o worker da simulação não conhece a área). Sem resposta, uma linha neutra.
 */
async function withPalcoRow(rows: AreaSummary[], live: boolean): Promise<AreaSummary[]> {
  if (!currentSession()?.areas.includes('palco') || rows.some((r) => r.area === 'palco')) return rows;
  const [{ palcoSummary }, { lastRoute }] = await Promise.all([
    import('./palco/summary'),
    import('@/lib/areaMemory'),
  ]);
  const row: AreaSummary = await palcoSummary().catch(() => ({
    area: 'palco' as const,
    line: live ? 'Ao vivo' : 'Suas VSLs',
    badge: null,
  }));
  const last = lastRoute('palco');
  return [...rows, last ? { ...row, lastRoute: `/palco${last === '/' ? '' : last}` } : row];
}

export function createSuite(
  kind: ProviderKind = providerKindFromUrl(),
  live: boolean = isLiveMode(),
): SuiteProvider {
  let automations: Promise<AutomationsProvider> | null = null;
  let studio: Promise<StudioProvider> | null = null;
  const read = new Set<Id>();
  async function baseSummary(): Promise<AreaSummary[]> {
    // V2-WP2: calculado no worker da suíte (mundo V2); sem ele, a fixture escrita à mão.
    if (kind === 'mock') {
      try {
        const [{ suiteClient }, { lastRoute }] = await Promise.all([
          import('./mock/suiteClient'),
          import('@/lib/areaMemory'),
        ]);
        const client = await suiteClient();
        const rows = await client.call<AreaSummary[]>('suite', 'summary', [], currentSession());
        return rows.map((a) => {
          const last = lastRoute(a.area);
          return last ? { ...a, lastRoute: `/${a.area}${last === '/' ? '' : last}` } : a;
        });
      } catch (err) {
        console.warn('[Rota] Resumo da suíte indisponível; usando a fixture.', err);
      }
    }
    const { suiteSummary } = await import('./suiteFixture');
    return suiteSummary(currentSession());
  }

  const suite: SuiteProvider = {
    auth: createAuth(live),
    painel: loadPainel,
    automacoes: () => {
      automations ??= loadAutomations(kind, live).catch((err: unknown) => {
        automations = null;
        throw err;
      });
      return automations;
    },
    estudio: () => {
      studio ??= loadStudio(kind).catch((err: unknown) => {
        studio = null;
        throw err;
      });
      return studio;
    },
    async summary(): Promise<AreaSummary[]> {
      const rows = await baseSummary();
      return withPalcoRow(rows, live);
    },
    notifications(onMessage: (n: SuiteNotification) => void): Unsubscribe {
      let off = false;
      const fromFixture = () =>
        import('./suiteFixture').then(({ suiteNotifications }) => suiteNotifications(currentSession()));
      const fromMock = () =>
        import('./mock/suiteClient')
          .then(({ suiteClient }) => suiteClient())
          .then((c) => c.call<SuiteNotification[]>('suite', 'notifications', [], currentSession()))
          .catch(fromFixture);
      void (kind === 'mock' ? fromMock() : fromFixture()).then((list) => {
        if (off) return;
        for (const n of list) onMessage({ ...n, read: read.has(n.id) });
      });
      return () => {
        off = true;
      };
    },
    async markRead(ids: Id[]): Promise<void> {
      for (const id of ids) read.add(id);
    },
  };
  return live ? withLiveSuite(suite) : suite;
}

/** Simulado: só no modo ao vivo, para Painel e Estúdio (as Automações são reais). */
const SIMULATED = ' · simulado';

/**
 * Áreas cujos avisos do sino são reais no modo ao vivo. Os do Painel saem daqui e o AppShell põe no
 * lugar as decisões reais (P0/P1); os das Automações e do Estúdio são da simulação e não entram:
 * contariam no selo e levariam a uma tela simulada.
 */
const LIVE_BELL_AREAS: ReadonlySet<Area> = new Set<Area>(['palco']);

/**
 * Lançador e sino no modo ao vivo: a linha das Automações vem do servidor; as do Painel e do Estúdio
 * continuam da simulação, marcadas como simuladas (a do Palco é real). No sino só passam os avisos
 * das áreas reais (`LIVE_BELL_AREAS`).
 */
export function withLiveSuite(suite: SuiteProvider): SuiteProvider {
  return {
    ...suite,
    async summary(): Promise<AreaSummary[]> {
      const rows = await suite.summary();
      const real = await suite
        .automacoes()
        .then(liveAutomationsSummary)
        .catch(() => null);
      return rows.map((a) => {
        // O Palco já é real no modo ao vivo (`/api/palco/*`).
        if (a.area === 'palco') return a;
        if (a.area !== 'automacoes')
          return a.line.endsWith(SIMULATED) ? a : { ...a, line: `${a.line}${SIMULATED}` };
        return real
          ? { ...real, ...(a.lastRoute ? { lastRoute: a.lastRoute } : {}) }
          : { ...a, line: 'Ao vivo', badge: null };
      });
    },
    notifications(onMessage: (n: SuiteNotification) => void): Unsubscribe {
      return suite.notifications((n) => {
        if (LIVE_BELL_AREAS.has(n.area)) onMessage(n);
      });
    },
  };
}

let suiteSingleton: SuiteProvider | null = null;

/** O provider da suíte deste navegador (um por aba). */
export function getSuite(): SuiteProvider {
  suiteSingleton ??= createSuite();
  return suiteSingleton;
}

/** Só nos testes: troca o provider da suíte. */
export function setSuiteForTests(s: SuiteProvider | null): void {
  suiteSingleton = s;
}
